import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Button, ErrorText, Field, Input, Select, Spinner, Textarea } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { formatDateTime } from '../../utils/format'
import type { AnamnesisField, AnamnesisSnapshot, AnamnesisTemplate, Encounter } from '../../types/api'

/**
 * Flujo abreviado (H2): el profesional elige una de SUS plantillas, el
 * formulario se arma solo con sus preguntas y lo que responde queda en la
 * ficha como instantánea (pregunta + respuesta), no como referencia a la
 * plantilla — que puede cambiar después.
 */

type AnswerValue = string | number | boolean | null

/** Valor inicial de un campo, reusando lo ya respondido si la plantilla coincide. */
function initialAnswers(fields: AnamnesisField[], previous?: AnamnesisSnapshot | null): Record<string, AnswerValue> {
  const saved = new Map(previous?.sections.flatMap((s) => s.fields.map((f) => [f.key, f.value] as const)) ?? [])
  return Object.fromEntries(
    fields.map((f) => [f.key, saved.get(f.key) ?? (f.type === 'BOOLEAN' ? false : '')]),
  )
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: AnamnesisField
  value: AnswerValue
  onChange: (value: AnswerValue) => void
}) {
  switch (field.type) {
    case 'TEXTAREA':
      return <Textarea rows={3} value={String(value ?? '')} placeholder={field.placeholder ?? ''} onChange={(e) => onChange(e.target.value)} />
    case 'NUMBER':
      return <Input type="number" value={String(value ?? '')} placeholder={field.placeholder ?? ''} onChange={(e) => onChange(e.target.value)} />
    case 'SELECT':
      return (
        <Select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>
      )
    case 'BOOLEAN':
      return (
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-brand-600" />
          Sí
        </label>
      )
    default:
      return <Input value={String(value ?? '')} placeholder={field.placeholder ?? ''} onChange={(e) => onChange(e.target.value)} />
  }
}

/** Vista de solo lectura de la anamnesis ya registrada. */
export function AnamnesisSummary({ snapshot }: { snapshot: AnamnesisSnapshot }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-steel-500">
        {snapshot.templateName} · registrada el {formatDateTime(snapshot.filledAt)}
      </p>
      {snapshot.sections.map((section) => (
        <div key={section.title}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-steel-500">{section.title}</p>
          <dl className="space-y-1 text-sm">
            {section.fields.map((f) => (
              <div key={f.key} className="flex gap-2">
                <dt className="shrink-0 font-medium text-ink">{f.label}:</dt>
                <dd className="text-steel-700">
                  {f.value === null || f.value === '' ? (
                    <span className="text-steel-400">sin registro</span>
                  ) : f.type === 'BOOLEAN' ? (
                    f.value ? 'Sí' : 'No'
                  ) : (
                    String(f.value)
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  )
}

export function AnamnesisForm({ encounter }: { encounter: Encounter }) {
  const queryClient = useQueryClient()
  const [templateId, setTemplateId] = useState(encounter.anamnesisTemplateId ?? '')
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(!encounter.anamnesis)

  const templates = useQuery({
    queryKey: ['anamnesis-templates'],
    queryFn: async () => (await api.get<AnamnesisTemplate[]>('/anamnesis-templates')).data,
  })

  const template = useMemo(
    () => (templates.data ?? []).find((t) => t.id === templateId),
    [templates.data, templateId],
  )
  const fields = useMemo(() => template?.sections.flatMap((s) => s.fields) ?? [], [template])

  // Al elegir plantilla se precarga el formulario (y se conserva lo ya escrito
  // cuyo campo exista también en la plantilla nueva).
  useEffect(() => {
    if (template) setAnswers(initialAnswers(fields, encounter.anamnesis))
  }, [template, fields, encounter.anamnesis])

  const save = useMutation({
    mutationFn: async () => (await api.post(`/encounters/${encounter.id}/anamnesis`, { templateId, answers })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] })
      setEditing(false)
      setError('')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const closed = encounter.status === 'CLOSED'

  if (!editing && encounter.anamnesis) {
    return (
      <div className="space-y-3">
        <AnamnesisSummary snapshot={encounter.anamnesis} />
        {!closed && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            <Icon name="blog" className="mr-1.5 inline h-4 w-4" />
            Volver a llenar
          </Button>
        )}
      </div>
    )
  }

  if (templates.isLoading) return <Spinner />

  if ((templates.data ?? []).length === 0) {
    return (
      <p className="rounded-lg bg-soft px-3 py-2 text-sm text-steel-700">
        Aún no tienes plantillas de anamnesis. Créalas en <strong>Mis plantillas</strong> y quedarán disponibles aquí.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <Field label="Plantilla" hint="Tus plantillas de anamnesis. El formulario se arma con sus preguntas.">
        <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={closed}>
          <option value="">Elige una plantilla…</option>
          {(templates.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </Select>
      </Field>

      {template && (
        <>
          {template.sections.map((section) => (
            <div key={section.title} className="rounded-lg border border-steel-100 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-steel-500">{section.title}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {section.fields.map((f) => (
                  <div key={f.key} className={f.type === 'TEXTAREA' ? 'sm:col-span-2' : ''}>
                    <Field label={f.required ? `${f.label} *` : f.label}>
                      <FieldInput
                        field={f}
                        value={answers[f.key] ?? (f.type === 'BOOLEAN' ? false : '')}
                        onChange={(value) => setAnswers((prev) => ({ ...prev, [f.key]: value }))}
                      />
                    </Field>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <Button variant="cta" disabled={save.isPending || closed} onClick={() => save.mutate()}>
              {save.isPending ? 'Guardando…' : 'Guardar anamnesis'}
            </Button>
            {encounter.anamnesis && (
              <Button variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>
            )}
          </div>
        </>
      )}

      <ErrorText>{error}</ErrorText>
    </div>
  )
}
